// Capa anticorrupción de Cobranza: cruza el wire de `loan-collector-assignment-business` con el
// directorio de la cadena (retail-org). Lo que no resuelve queda en `null` — nunca se rellena.
import type { CollectorAssignmentResponse } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/responses/CollectorAssignmentResponse';
import type { CollectorLoadResponse } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/responses/CollectorLoadResponse';
import type { RetailUser } from '@fiado/type-kit/bin/retailOrg/dtos/RetailUser';
import type { Store } from '@fiado/type-kit/bin/retailOrg/dtos/Store';
import type { AssignmentRow, CollectorLoadRow, DirectoryPresence } from '../types';

/** Directorio de la cadena, indexado por `retailUserId` — el id con el que viaja `collectorUserId`. */
export type RetailUsersById = ReadonlyMap<string, RetailUser>;

export type StoresById = ReadonlyMap<string, Store>;

/** El directorio de la cadena tal como llegó, con la bandera de si se pudo leer. */
export interface RetailOrgSnapshot {
    retailUsersById: RetailUsersById;
    storesById: StoresById;
    /**
     * El directorio no respondió. Con esto prendido los mapas están vacíos porque NO se pudo
     * preguntar, no porque las personas no existan: la diferencia llega hasta la etiqueta.
     */
    unavailable: boolean;
}

/** Qué sabemos del id: existe, no existe, o el directorio no respondió y nadie lo verificó. */
function presenceOf(userId: string, directory: RetailOrgSnapshot): DirectoryPresence {
    if (directory.unavailable) return 'UNAVAILABLE';
    return directory.retailUsersById.has(userId) ? 'KNOWN' : 'UNKNOWN';
}

/** Nombre del usuario retail, o `null` cuando no está en el directorio o no tiene etiqueta. */
function displayNameOf(userId: string, directory: RetailOrgSnapshot): string | null {
    return directory.retailUsersById.get(userId)?.displayName ?? null;
}

/** La carga de un cobrador, cruzada con el directorio de la cadena. */
export function toCollectorLoadRow(
    dto: CollectorLoadResponse,
    directory: RetailOrgSnapshot,
): CollectorLoadRow {
    const user = directory.retailUsersById.get(dto.collectorUserId);
    return {
        collectorUserId: dto.collectorUserId,
        displayName: user?.displayName ?? null,
        seniority: user?.collectorSeniority ?? null,
        activeAssignments: dto.activeAssignments,
        threshold: dto.threshold,
        storeIds: dto.storeIds,
        storeNames: dto.storeIds
            .map((storeId) => directory.storesById.get(storeId)?.name)
            .filter((name): name is string => name !== undefined),
        presence: presenceOf(dto.collectorUserId, directory),
    };
}

/** Una asignación con el cobrador, la tienda y el cobrador de origen resueltos a nombre legible. */
export function toAssignmentRow(
    dto: CollectorAssignmentResponse,
    directory: RetailOrgSnapshot,
): AssignmentRow {
    const from = dto.reassignedFromUserId;
    return {
        ...dto,
        collectorDisplayName: displayNameOf(dto.collectorUserId, directory),
        collectorPresence: presenceOf(dto.collectorUserId, directory),
        storeName: directory.storesById.get(dto.storeId)?.name ?? null,
        reassignedFromDisplayName: from === null ? null : displayNameOf(from, directory),
        reassignedFromPresence: from === null ? 'UNKNOWN' : presenceOf(from, directory),
    };
}
